import { useCallback, useEffect, useState } from 'react'
import { usePatient } from '../context/PatientContext'
import * as svc from '../services/medicinesService'
import { parseUtcDate } from '../utils/dates'

export function useMedicinesList() {
  const { currentProfile } = usePatient()
  const [list, setList] = useState(null)
  const [today, setToday] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const refresh = useCallback(async () => {
    if (!currentProfile) {
      setList(null)
      setToday(null)
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    try {
      const [l, t] = await Promise.all([
        svc.listMedicines(currentProfile),
        svc.todaysDoses(currentProfile),
      ])
      setList(l)
      setToday(t)
    } catch (e) {
      setError(e)
    } finally {
      setLoading(false)
    }
  }, [currentProfile])

  useEffect(() => {
    setList(null)
    setToday(null)
    refresh()
  }, [refresh])

  const create = useCallback(
    async (body) => {
      const c = await svc.createMedicine(currentProfile, body)
      await refresh()
      return c
    },
    [currentProfile, refresh],
  )

  const recordIntake = useCallback(
    async (id, body) => {
      const r = await svc.recordIntake(currentProfile, id, body)
      await refresh()
      return r
    },
    [currentProfile, refresh],
  )

  return { list, today, loading, error, refresh, create, recordIntake }
}

export function useMedicine(id) {
  const { currentProfile } = usePatient()
  const [medicine, setMedicine] = useState(null)
  const [history, setHistory] = useState([])
  const [sideEffects, setSideEffects] = useState([])
  const [loading, setLoading] = useState(true)

  const refresh = useCallback(async () => {
    if (!currentProfile || !id) {
      setMedicine(null)
      setLoading(false)
      return
    }
    setLoading(true)
    try {
      const m = await svc.getMedicine(currentProfile, id)
      setMedicine(m)
      // Fetch last 14 days of intake history
      const to = new Date().toISOString()
      const from = parseUtcDate(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString()
      const [h, se] = await Promise.all([
        svc.intakeHistory(currentProfile, id, from, to),
        svc.listSideEffects(currentProfile, id),
      ])
      setHistory(h)
      setSideEffects(se)
    } finally {
      setLoading(false)
    }
  }, [currentProfile, id])

  useEffect(() => {
    setMedicine(null)
    refresh()
  }, [refresh])

  const recordSideEffect = useCallback(
    async (body) => {
      await svc.recordSideEffect(currentProfile, id, body)
      await refresh()
    },
    [currentProfile, id, refresh],
  )

  const update = useCallback(
    async (patch) => {
      await svc.updateMedicine(currentProfile, id, patch)
      await refresh()
    },
    [currentProfile, id, refresh],
  )

  return { medicine, history, sideEffects, loading, refresh, recordSideEffect, update }
}
